function normalizeIdList(value) {
  if (!Array.isArray(value)) {
    return []
  }

  return [...new Set(value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item)))].sort((left, right) => left - right)
}

function normalizeBoolean(value, defaultValue = true) {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return value !== 0
  }
  if (typeof value === 'string') {
    return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase())
  }
  return defaultValue
}

function buildComparableDraft(draft = {}) {
  return {
    report_template_key: String(draft.report_template_key || 'inventory_action').trim(),
    filter_name: String(draft.filter_name || '').trim(),
    report_peroid: String(draft.report_peroid || ''),
    start_period: Number.isFinite(Number(draft.start_period)) ? Number(draft.start_period) : 0,
    month_span_forward: Number.isFinite(Number(draft.month_span_forward)) ? Number(draft.month_span_forward) : 3,
    sort: String(draft.sort || 'LOCATION').trim().toUpperCase(),
    view_mode: String(draft.view_mode || 'Retail').trim(),
    locations: normalizeIdList(draft.locations),
    categories: normalizeIdList(draft.categories),
    classes: normalizeIdList(draft.classes),
    include_summary: normalizeBoolean(draft.include_summary, true),
  }
}

function humanizeSection(section) {
  switch (section) {
    case 'naming':
      return 'Filter name'
    case 'template':
      return 'Template'
    case 'timeframe':
      return 'Timeframe'
    case 'view_mode':
      return 'View mode'
    case 'sort':
      return 'Sort'
    case 'scope':
      return 'Scope'
    case 'summary_option':
      return 'Saved summary'
    default:
      return section
    }
}

function humanizeSort(sort) {
  switch (String(sort || '').toUpperCase()) {
    case 'CLASS':
      return 'By class'
    case 'LOCATION':
      return 'By location'
    case 'EXECUTIVE':
      return 'By executive'
    default:
      return sort
  }
}

function formatTimeframe(snapshot) {
  const months = snapshot.month_span_forward === 1 ? 'month' : 'months'
  return `${snapshot.month_span_forward} ${months} beginning from ${snapshot.report_peroid} (offset ${snapshot.start_period})`
}

function formatScope(snapshot) {
  const segments = []
  if (snapshot.locations.length > 0) {
    segments.push(`locations: ${snapshot.locations.join(', ')}`)
  }
  if (snapshot.categories.length > 0) {
    segments.push(`categories: ${snapshot.categories.join(', ')}`)
  }
  if (snapshot.classes.length > 0) {
    segments.push(`classes: ${snapshot.classes.join(', ')}`)
  }

  return segments.length > 0 ? segments.join('; ') : 'Full client scope'
}

export function summarizeDraftDiff(originalDraft, currentDraft) {
  if (!originalDraft || !currentDraft) {
    return null
  }

  const before = buildComparableDraft(originalDraft)
  const after = buildComparableDraft(currentDraft)
  const changeItems = []
  const changedSections = []

  if (before.filter_name !== after.filter_name) {
    changeItems.push({ section: 'naming', label: 'Filter name', before: before.filter_name, after: after.filter_name })
    changedSections.push('naming')
  }

  if (before.report_template_key !== after.report_template_key) {
    changeItems.push({ section: 'template', label: 'Template', before: before.report_template_key, after: after.report_template_key })
    changedSections.push('template')
  }

  if (
    before.report_peroid !== after.report_peroid
    || before.start_period !== after.start_period
    || before.month_span_forward !== after.month_span_forward
  ) {
    changeItems.push({ section: 'timeframe', label: 'Timeframe', before: formatTimeframe(before), after: formatTimeframe(after) })
    changedSections.push('timeframe')
  }

  if (before.view_mode !== after.view_mode) {
    changeItems.push({ section: 'view_mode', label: 'View mode', before: before.view_mode, after: after.view_mode })
    changedSections.push('view_mode')
  }

  if (before.sort !== after.sort) {
    changeItems.push({ section: 'sort', label: 'Sort', before: humanizeSort(before.sort), after: humanizeSort(after.sort) })
    changedSections.push('sort')
  }

  if (
    JSON.stringify(before.locations) !== JSON.stringify(after.locations)
    || JSON.stringify(before.categories) !== JSON.stringify(after.categories)
    || JSON.stringify(before.classes) !== JSON.stringify(after.classes)
  ) {
    changeItems.push({ section: 'scope', label: 'Scope', before: formatScope(before), after: formatScope(after) })
    changedSections.push('scope')
  }

  if (before.include_summary !== after.include_summary) {
    changeItems.push({
      section: 'summary_option',
      label: 'Saved summary',
      before: before.include_summary ? 'Included' : 'Not included',
      after: after.include_summary ? 'Included' : 'Not included',
    })
    changedSections.push('summary_option')
  }

  const uniqueSections = [...new Set(changedSections)]
  const labels = uniqueSections.map(humanizeSection)
  const hasChanges = changeItems.length > 0

  return {
    has_changes: hasChanges,
    regeneration_required: hasChanges,
    changed_sections: uniqueSections,
    changed_section_labels: labels,
    change_items: changeItems,
    summary: hasChanges
      ? `Detected changes in ${labels.map((label) => label.toLowerCase()).join(', ')}. Regeneration is required before the report file reflects this draft.`
      : 'No material draft changes were detected compared with the loaded filter. Regeneration is not required.',
    short_summary: hasChanges
      ? `Changed ${labels.map((label) => label.toLowerCase()).join(', ')}. Regeneration required.`
      : 'No material changes detected. Regeneration is not required.',
  }
}

export function buildComparableDraftFromFormPayload(formDraft) {
  return buildComparableDraft(formDraft)
}

